// ============================================================================
// ModuleErrorBoundary — aísla el fallo de un módulo (addon lazy)
// ----------------------------------------------------------------------------
// Un error al cargar o renderizar un módulo (Meta, Crawl, Creatividades...) ya
// no tumba la suite entera vía el ErrorBoundary global de index.js: se pinta un
// bloque dentro del layout, con la cabecera y las pestañas operativas.
// Se resetea cuando cambia `resetKey` (el padre pasa location.pathname).
// ============================================================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';
import { isChunkLoadError } from '../utils/lazyRetry';
import '../styles/module-error.css';

class ModuleErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 Error capturado por ModuleErrorBoundary:', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);
    return (
      <div className="module-error" role="alert">
        <div className="module-error__head">
          <AlertTriangle size={20} className="module-error__icon" aria-hidden="true" />
          <h2 className="module-error__title">Este módulo ha fallado</h2>
        </div>
        <p className="module-error__message">
          {chunk
            ? 'La aplicación se ha actualizado. Recarga para continuar.'
            : 'Ha ocurrido un error inesperado al mostrar esta sección. El resto de la suite sigue disponible.'}
        </p>
        <div className="module-error__actions">
          <button
            type="button"
            className="module-error__btn module-error__btn--primary"
            onClick={() => window.location.reload()}
          >
            <RefreshCw size={16} aria-hidden="true" />
            Recargar
          </button>
          <button
            type="button"
            className="module-error__btn module-error__btn--secondary"
            onClick={this.props.onBack}
          >
            <ArrowLeft size={16} aria-hidden="true" />
            Volver al tablero
          </button>
        </div>
      </div>
    );
  }
}

export default function ModuleErrorBoundary({ resetKey, children }) {
  const navigate = useNavigate();
  return (
    <ModuleErrorBoundaryInner resetKey={resetKey} onBack={() => navigate('/task')}>
      {children}
    </ModuleErrorBoundaryInner>
  );
}
